import { inject, Injectable, RESPONSE_INIT, signal } from '@angular/core';
import { NavigationError, RedirectCommand, Router } from '@angular/router';

/** Where a failed first navigation is sent, so it still finishes with the message and the status. */
export const PAGE_UNAVAILABLE = '/page-unavailable';

/** Why the last navigation shows nothing new: the API did not answer for its page. */
@Injectable({ providedIn: 'root' })
export class PageFailure {

  readonly message = signal<string | undefined>(undefined);
  /** The API knows no shop for this address, which trying again will not change. */
  readonly missing = signal(false);

}

/**
 * The router's handler for a page that could not be fetched. Without it a later navigation just
 * does nothing, and a first one renders a blank page, since the shell waits for the shop's data.
 */
export function pageFailed(error: NavigationError): RedirectCommand | undefined {
  const status = (error.error as { status?: number } | null)?.status;
  const responseInit = inject(RESPONSE_INIT, { optional: true });
  if (responseInit) {
    responseInit.status = status === 404 ? 404 : 503;
  }
  const failure = inject(PageFailure);
  failure.missing.set(status === 404);
  failure.message.set(status === 404
    ? 'Магазину за цією адресою немає.'
    : 'Не вдалося відкрити сторінку. Спробуйте ще раз трохи згодом.');
  // A failed first navigation fails the whole server render, which then answers a bare 404, and in
  // the browser rolls the address back to the root. A later one just stays on the page it was on.
  const router = inject(Router);
  return router.navigated
    ? undefined
    : new RedirectCommand(router.parseUrl(PAGE_UNAVAILABLE), { skipLocationChange: true });
}
